import { formsValidationErrors, isEmptyOrSpaces } from '@/helpers/form';
import axios from 'axios';
import VeeValidate, { Validator } from 'vee-validate';
import ar from 'vee-validate/dist/locale/ar';
import Vue from 'vue';
import { apiModule, isValidHijriDate } from '@/helpers/utils';
import { socialIdentityTypeEnum } from '@/helpers/enum';
import { calculateAge } from '@/modules/global/helpers/utils';
const addExtraMessagesToLocal = (local, extraMessages) => {
  return Object.assign({}, local, { messages: Object.assign({}, local.messages, extraMessages) });
};

Vue.use(VeeValidate, {
  locale: 'ar',
  dictionary: {
    ar: addExtraMessagesToLocal(ar, {
      numeric: label => `${label} يجب أن يكون أرقام فقط`,
      digits: (label, value) => `${label} يجب أن يكون أرقام فقط ومكون من ${value} أرقام`,
      min: (label, value) => `${label} يجب أن يحتوي على ${value} حرف على الأقل`,
      max: (label, value) => `${label} يجب أن يحتوي على ${value} حرف على الأكثر`,
    }),
  },
  fieldsBagName: 'fieldsBag',
});

Validator.extend('arabic', {
  getMessage: () => formsValidationErrors.writeArabicOnly,
  validate: value =>
    !!value &&
    !/[^\u0600-\u06FF\d\s\`\~\!\@\#\$\%\^\&\*\(\)\-\_\=\+\<\>\|\[\{\]\}\;\:\'\"\\\|\,\<\.\>\/\?]/g.test(
      value
    ) &&
    /[\u0600-\u06FF]/.test(value),
});

Validator.extend('no_english_letters', {
  getMessage: () => 'لا يمكن أن يحتوي على رموز خاصة حروف انجليزية',
  validate: value => !!value && !/[A-Za-z]/g.test(value),
});

Validator.extend('arabic_letters', {
  getMessage: () => 'يرجى إدخال حروف عربية',
  validate: value => !!value && !/[^\u0600-\u065F\u066A-\u06EF\u06FA-\u06FF\s]/g.test(value),
});
Validator.extend('english_letters', {
  getMessage: () => 'يرجى إدخال حروف انجليزية فقط بدون مسافات',
  validate: (value) => { return /^[A-Za-z.][A-Za-z0-9.]*$/.test(value); },
});
Validator.extend('english_letters_numbers', {
  getMessage: () => 'يرجى إدخال حروف انجليزية فقط بدون مسافات',
  validate: (value) => { return /^[A-Za-z0-9]+$/.test(value); },
});
Validator.extend('no_special_char', {
  getMessage: () => 'لا يمكن أن يحتوي على رموز خاصة',
  validate: value =>
    !!value &&
    !/[\\`\~\!\@\#\$\%\^\&\*\(\)\-\_\=\+\<\>\|\[\{\]\}\;\:\'\"\\\|\,\<\.\>\/\?]/g.test(value),
});
Validator.extend('checkIfFirstCharStartsBy', {
  paramNames: ['number'],
  getMessage: (field, number) => formsValidationErrors.shouldNumberStartBy(number?.toString()),
  validate: (value, { number }) => !!value && value.toString()[0] == number,
});
Validator.extend('socialId', {
  paramNames: ['typeId'],
  getMessage(field, args, data) {
    return data.msg;
  },
  validate(value, { typeId }) {
    const socialTypeId = typeId.replace('\n', '')
      .trim();
    const socialTypeIdsLimitedLength = [socialIdentityTypeEnum.nationalId, socialIdentityTypeEnum.regularResidency, socialIdentityTypeEnum.borderNumber];
    const regexSpecialCharacters = /[\\`\~\!\@\#\$\%\^\&\*\(\)\-\_\=\+\<\>\|\[\{\]\}\;\:\'\"\\\|\,\<\.\>\/\?]/g;
    const englishLetters = /^[A-Za-z0-9]*$/;
    if (!englishLetters.test(value))
      return { data: { msg: formsValidationErrors.shouldEnglishLettersOrNumbers } };
    if (regexSpecialCharacters.test(value))
      return { data: { msg: formsValidationErrors.noSpecialCharacter } };
    if (value.trim().length != 10 && socialTypeIdsLimitedLength.indexOf(+socialTypeId) !== -1)
      return { data: { msg: formsValidationErrors.shouldSocialIdNumberIn10Digits } };
    if (socialTypeId == socialIdentityTypeEnum.nationalId && !!value && !value.startsWith('1'))
      return { data: { msg: formsValidationErrors.shouldNumberStartBy('1') } };
    else if (socialTypeId == socialIdentityTypeEnum.regularResidency && !!value && !value.startsWith('2'))
      return { data: { msg: formsValidationErrors.shouldNumberStartBy('2') } };
    else
      return { valid: true };
  },
});
Validator.extend('isVerified', {
  paramNames: ['isVerified'],
  getMessage: field => 'الرجاء التحقق من البيانات',
  validate: (value, { isVerified }) => isVerified == 'true',
});
Validator.extend('phone', {
  getMessage: field => formsValidationErrors.shouldPhoneNumberStartWith05,
  validate: value => !!value && value.length && value[0] == 0 && value[1] == 5,
});
Validator.extend('string', {
  paramNames: ['length'],
  getMessage: field => `${field} تجاوز الحد المسموح`,
  validate: (value, { length }) => {
    return (value || '').toString().length <= (+length || 100);
  },
});
Validator.extend('int', {
  paramNames: ['disabled'],
  getMessage: field => `${field} تجاوز الحد المسموح`,
  validate: (value, { disabled }) => disabled == 'true' || value <= 2147483648,
});
Validator.extend('number', {
  paramNames: ['disabled'],
  getMessage: () => 'يجيب أن يحتوي على أرقام فقط',
  validate: (value, { disabled }) => disabled == 'true' || value <= 2147483648,
});
Validator.extend('long', {
  paramNames: ['disabled'],
  getMessage: field => `${field} تجاوز الحد المسموح`,
  validate: (value, { disabled }) => disabled == 'true' || value <= 9223372036854775808,
});
Validator.extend('hijri_year', {
  paramNames: ['gregorianYear'],
  getMessage: label => `${label} يجب أن يكون أصغر من أو يساوي العام الحالي`,
  validate: (value, { gregorianYear }) => +value <= +getHijriYear(gregorianYear),
});
Validator.extend('age', {
  paramNames: ['year'],
  getMessage: (field, year) => formsValidationErrors.age(year),
  validate: (value, { year }) => isValidHijriDate(value) || Math.floor(calculateAge(value)) === +year,
});
Validator.extend('max_age', {
  paramNames: ['year'],
  getMessage: (field, year) => formsValidationErrors.max_age(year),
  validate: (value, { year }) => isValidHijriDate(value) || calculateAge(value) < +year,
});
Validator.extend('min_age', {
  paramNames: ['year'],
  getMessage: (field, year) => formsValidationErrors.min_age(year),
  validate: (value, { year }) => isValidHijriDate(value) || calculateAge(value) >= +year,
});
Validator.extend('not_equal', {
  paramNames: ['otherValue', 'message'],
  getMessage: (label, [otherValue, message]) => `${message ? message : (label + ' يجب ألا يساوي ' + otherValue)}`,
  validate: (value, { otherValue }) => value !== otherValue,
});
Validator.extend('error', {
  paramNames: ['errorMessage'],
  validate: () => false,
  getMessage: (label, errorMessage) => errorMessage,
});
Validator.extend('isValid', {
  paramNames: ['errorMessage', 'valid'],
  validate: (value, { valid }) => (String(valid) || '').trim() == 'true',
  getMessage: (label, [errorMessage]) => errorMessage, // the first param (errorMessage)
});
Validator.extend(
  'conditionalRequired',
  {
    paramNames: ['validationEnabled'],
    validate: (value, { validationEnabled }) => {
      const returnValue =
        !validationEnabled || validationEnabled == 'false' ? true : !isEmptyOrSpaces(value);
      return {
        valid: returnValue,
        data: { required: !returnValue },
      };
    },
    getMessage: field => `${field} مطلوب`,
  },
  { computesRequired: true }
);
Validator.extend('external_validation', {
  paramNames: ['requestType', 'module', 'endpoint', 'expectedStatus', 'expectedResults'],
  validate: (value, params) => {
    const modulePrefix = params.module ? apiModule[params.module] : '';
    return axios[params.requestType](`${modulePrefix}${params.endpoint}`, { params: { value } })
      .then(response => dynamicValidatorHandler(response, params))
      .catch(({ response }) => dynamicValidatorHandler(response, params));
  },
  getMessage: (field, params, msg) => 'الحقل غير صالح',
});

Validator.extend('isValidTechnicalName', {
  getMessage: label => `${label} يجب أن يكون كلمة انجليزية بدون مسافات و تبدأ بحرف صغير مثل "camelCase"`,
  validate: (value) => {
    return /^[a-z]+[a-zA-Z0-9]*$/.test(value);
  },
});

Validator.extend('no_gov_email', {
  getMessage: () => 'عفواً لايمكنك إضافة بريد لجهة رسمية',
  validate: value => !value.toLowerCase()
    .endsWith('gov.sa'),
});

Validator.extend('only-digits', {
  getMessage: (label, value) => `${label} يجب أن يحتوي على ${value} أرقام فقط`,
  validate: value => /^\d+$/.test(value),
});

const getHijriYear = (gregorianYear) => {
  const year = Number(gregorianYear);
  return new Intl.DateTimeFormat('ar-u-ca-islamic', { year: 'numeric' })
    .format(!isNaN(year) && year ? new Date(Date.UTC(year)) : new Date())
    .replace(/\D/g, '');
};

const dynamicValidatorHandler = (res, params) => {
  let isValid = true;
  if (params.expectedStatus) {
    isValid = params.expectedStatus == res.status;
  }
  if (params.expectedResults) {
    isValid = params.expectedResults == res.data;
  }
  return {
    valid: isValid,
    data: res.data,
  };
};
export const veeValidator = Validator;
